/**
 * English strings of the preview package (W2-02), namespace 'preview': panel and toolbar, overlays (tooltips, labels,
 * gizmo key-rule hints), Theater, test play, judged-play results, Phira player settings, the "Preview differs from
 * export" banner, commands, settings rows and help topics. zh-CN lives in src/i18n/zh-CN/preview.ts (localisation
 * package). ICU-lite syntax (src/i18n/icu.ts); no HTML.
 */

import { catalogOf, defineMessages } from '../../i18n/define';

export const m = defineMessages('preview', {
  // ---- panel and toolbar ----
  panelTitle: 'Preview',
  toolbarLabel: 'Preview tools',
  emptyTitle: 'No chart open',
  emptyBody: 'Open or create a chart to see it play here exactly as Phira shows it.',
  elsewhereTheater: 'The preview is shown in Theater.',
  elsewhereTestPlay: 'The preview is shown in test play.',
  screenMenu: 'Screen shape',
  screenButton: 'Screen shape: {screen}',
  screenChart: 'Chart ({aspect})',
  screenFill: 'Fill the panel',
  screenAs: 'As seen on {shape}',
  screenOptionChart: 'The chart aspect ratio',
  screenOptionFill: 'Fill the panel',
  screenOptionAs: 'As seen on a {shape} screen',
  screenOpt169: 'As seen on a 16:9 screen',
  screenOpt43: 'As seen on a 4:3 screen',
  screenOpt32: 'As seen on a 3:2 screen',
  screenOpt195: 'As seen on a 19.5:9 phone',
  screenOpt219: 'As seen on a 21:9 screen',
  modeLabel: 'Preview mode',
  modeClean: 'Clean',
  modeEdit: 'Edit',
  moreMenu: 'More preview options',
  snapMenu: 'Gizmo snapping',
  snapGrid: 'Snap to the grid ({size} px)',
  snapGridSize: 'Grid size',
  snapGridSizeOption: '{size} px',
  snapCentre: 'Snap to the screen centre',
  snapEdges: 'Snap to the screen edges',
  snapAnchors: 'Snap to other lines',
  snapReset: 'Reset snapping',
  zoomPercent: '{percent}%',
  testMenu: 'Test play options',
  moreTitle: 'Preview tools',
  testAutoplay: 'Test: watch (autoplay)',
  testJudged: 'Test: play it yourself (judged)',
  testWatchShort: 'Watch',
  testPlayShort: 'Play',
  testWatchWide: 'Test: watch',
  testPlayWide: 'Test: play',
  testChooseTitle: 'How do you want to test the chart?',
  testChooseBody: 'You can switch any time in the menu next to the Test button or in the pause menu.',
  testChooseWatch: 'Watch it (autoplay)',
  testChooseWatchDesc: 'The chart plays itself as Phira autoplay shows it. Good for checking how it looks.',
  testChoosePlay: 'Play it myself (judged)',
  testChoosePlayDesc:
    'Tap with any letter key or the mouse, flick with Space. You get your accuracy and early / late timing.',
  fromPlayhead: 'From 2 beats before the playhead',
  fromStart: 'From the chart start',
  argFrom: 'Start test play',
  cancel: 'Cancel',
  percentValue: '{value}%',

  // ---- commands ----
  cmdTheater: 'Theater',
  cmdTestPlay: 'Test play',
  cmdToggleMode: 'Switch the preview between Clean and Edit',
  cmdHud: 'Show the in-game HUD',
  cmdLabels: 'Show line labels',
  cmdSafeArea: 'Safe-area guide',
  cmdGhosts: 'Show hidden notes',
  cmdInvisibleLines: 'Show invisible lines',
  cmdGizmo: 'Show the gizmo',
  cmdAutoKey: 'Auto-key',
  cmdSampleMode: 'Sample mode',
  cmdLaneLink: 'Link the lane and the preview (hover markers)',
  cmdScreen: 'Preview screen shape',
  cmdPhiraSettings: 'Phira player settings',
  cmdReadout: 'Line readout',
  cmdResetView: 'Reset the preview zoom',
  cmdZoomIn: 'Zoom the preview in',
  cmdZoomOut: 'Zoom the preview out',
  cmdJudged: 'Judged test play',
  cmdForceAspect: 'Same aspect ratio for every player (forceAspectRatio)',
  cmdAddLineHere: 'Add line here',
  cmdMakeActive: 'Make active',
  cmdSetParent: 'Set parent...',
  cmdClearParent: 'Remove parent',
  cmdSelectLineNotes: 'Select its notes',
  cmdClearFilters: 'Show everything (clear preview filters)',
  reasonNoProject: 'Open a chart first.',
  reasonEditMode: 'Switch the preview to Edit to use this.',
  effectsApproximateList: 'Shown now: {names}.',
  effectsNotPreviewed: 'Effect not previewed',
  effectsNotPreviewedList: 'Drawn without these effects: {names}.',
  reasonNoLineHere: 'There is no line here.',
  reasonNoParent: 'This line has no parent.',
  reasonNoNotes: 'This line has no notes.',
  toastSelectedNotes: '{count, plural, one {Selected # note} other {Selected # notes}}',
  safeAreaNone: 'No safe-area guide',
  safeAreaOption: '{area} safe area',
  safeAreaLabel: '{aspect} safe area',

  // ---- overlay ----
  ghostCover: 'Hidden: the note is under its line and "Hide notes under the line" is on (RPE isCover).',
  ghostNegativeLineAlpha: 'Hidden: the line opacity is below 0, so Phira hides its notes (they are still judged).',
  ghostCulled:
    "Hidden: culled by Phira's aggressive culling (a player setting, on by default): it stops at the first note of this speed past the screen edge.",
  ghostUiMoverLine: 'Hidden: notes on a UI mover line are never drawn.',
  ghostBeforeVisibleTime: 'Hidden: before its appear time (visible time).',
  ghostOffscreen: 'Off screen at this moment.',
  ghostFiltered: 'Hidden by a preview filter (hidden or solo lines).',
  invisibleAlpha: '{label}: opacity 0 or below, Phira draws nothing.',
  invisibleScale: '{label}: its size is 0, Phira draws nothing.',
  invisibleEmptyText: '{label}: the text is empty, Phira draws nothing.',
  invisiblePaintCleared: '{label}: its brush is 0 or less, so its painting is cleared.',
  invisibleUiMover: '{label}: a UI mover; it moves the {element} HUD element instead of drawing itself.',
  hudMoves: 'Moves: {element}',
  hudPause: 'pause button',
  hudComboNumber: 'combo number',
  hudCombo: 'COMBO text',
  hudScore: 'score',
  hudBar: 'progress bar',
  hudName: 'song name',
  hudLevel: 'level',
  laneX: 'X {x}',
  offscreen: '{label} (off screen)',
  labelsHidden:
    '{count, plural, one {+# label hidden (hover a line to see it)} other {+# labels hidden (hover a line to see its label)}}',
  sampleStart: 'Start',
  sampleEnd: 'End',
  sampleStartHelp: 'Drag to change where the selected segment starts.',
  sampleEndHelp: 'Drag to change where the selected segment ends.',
  sampleHoldEnd: 'This segment stays, then jumps: its end follows its start.',
  lineLocked: 'This line is locked (Lines panel). Unlock it to edit it.',
  parentPickHint: 'Click the new parent line, or empty space for no parent.',
  parentPromptTitle: 'Set the parent',
  parentKeepPlace: 'Keep its place on screen',
  parentKeepPlaceHelp: 'Its position is converted into the parent frame, so it does not move now.',
  parentKeepNumbers: 'Keep its numbers',
  parentKeepNumbersHelp: 'Its values stay as they are (RPE behaviour); it moves with the parent.',

  // ---- gizmo ----
  propX: 'X',
  propY: 'Y',
  propRotate: 'Rotation',
  propOpacity: 'Opacity',
  propLength: 'Length',
  propWidth: 'Width',
  propHeight: 'Height',
  propSpeed: 'Speed',
  propOther: 'Value',
  ruleKeyNew: '{axis}: new keyframe at {beat}',
  ruleKeyUpdate: '{axis}: updates the keyframe at {beat}',
  ruleWhole: '{axis}: whole chart',
  ruleWholeNew: '{axis}: whole chart (new track)',
  ruleCreateAt: '{axis}: new track, keyframe at {beat} (stays as before until then)',
  ruleOffset: '{axis}: {count, plural, one {moves # keyframe} other {moves # keyframes}}',
  ruleOffsetSelected: '{axis}: {count, plural, one {moves # selected keyframe} other {moves # selected keyframes}}',
  ruleTooltip: 'Dragging now: {rules}',
  hintLock: 'lock to one axis',
  hint15: '15-degree steps',
  hintWidthOnly: 'width only',
  hintOffset: 'offset the whole motion',
  hintNoSnap: 'no snapping',
  hintCancel: 'cancel',
  summaryAxis: '{axis} {value}',
  summaryRotate: '{value} deg',
  summaryScale: '{value}%',
  txnMove: 'Move line',
  txnRotate: 'Rotate line',
  txnLength: 'Change line length',
  txnSize: 'Resize line',
  txnOpacity: 'Change line opacity',
  txnOffset: 'Offset motion',
  txnSample: 'Move segment point',
  txnAddLine: 'Add line',
  txnSetParent: 'Set parent',
  txnClearParent: 'Remove parent',
  txnForceAspect: 'Change forceAspectRatio',

  // ---- readout ----
  readoutLabel: 'Active line at the playhead',
  readoutNoLine: 'No active line: click a line in the preview or the Lines panel.',
  readoutDeg: '{value} deg',
  readoutAbsent: 'not set',
  readoutCross: '(crosses in {sec} s)',
  readoutNeverCrosses: '(never crosses)',
  propRotateShort: 'Rot',
  propOpacityShort: 'Op',
  propSpeedShort: 'Spd',
  opacitySlider: 'Opacity',

  // ---- Phira settings ----
  phiraTitle: 'Phira player settings',
  phiraNote: 'Preview only: what players with these settings see. Never written to the chart.',
  phiraAspect: 'Aspect ratio (aspect_ratio)',
  phiraAspectChart: 'Same as the chart ({aspect})',
  phiraNoteScale: 'Note size (note_scale)',
  phiraNoteScaleValue: '{value}x',
  phiraDoubleHint: 'Highlight simultaneous notes (double hint)',
  phiraParticles: 'Particles',
  phiraAggressive: 'Aggressive note culling',
  phiraStrictAutoplay: 'Autoplay hit colours as Phira (ignore tintHitEffects)',
  phiraMsaa: 'Anti-aliasing (MSAA)',
  /** Phira `apFcIndicator` (R§3.4): the judge-line colour follows the Perfect / Good / miss ladder in judged play. */
  phiraApFc: 'AP/FC judge-line indicator',
  phiraDimOverride: 'Override the background dim (chart: {value}%)',
  phiraDim: 'Background dim',
  phiraReset: 'Reset to Phira defaults',

  // ---- banner ----
  bannerDiffers: 'Preview differs from export: {parts}.',
  bannerHidden: '{count, plural, one {# line hidden} other {# lines hidden}}',
  bannerSolo: '{count, plural, one {# line soloed} other {# lines soloed}}',
  bannerMuted: '{count, plural, one {# layer muted} other {# layers muted}}',
  bannerDim: 'other lines dimmed',
  bannerShowAll: 'Show everything',
  listJoin: '{a}, {b}',

  // ---- Theater ----
  theaterLabel: 'Theater',
  theaterExit: 'Back to the editor',
  theaterHintPlay: 'plays and pauses',
  theaterHintStop: 'stops and goes back to the editor',
  theaterHintExit: 'back to the editor, still playing',
  theaterControls: 'Theater controls',

  // ---- test play ----
  testPlayLabel: 'Test play',
  pauseButton: 'Pause (Esc)',
  pauseTitle: 'Paused',
  resume: 'Resume',
  restart: 'Restart',
  backHere: 'Back to editor here',
  backStart: 'Back to where I started',
  modeTitle: 'Mode',
  modeAutoplay: 'Autoplay',
  modeJudged: 'Play (judged)',
  rate: 'Rate',
  rateOption: '{rate}x',
  judgedHelp:
    'Tap with any letter key or the left mouse button. Hold the key for drags and holds. Flick: move the mouse while holding, or press Space. Esc pauses.',
  autoplayHelp: 'Autoplay: the chart plays as Phira plays it. Esc or Space pauses.',
  gradeAnnounce: '{grade, select, perfect {Perfect} good {Good} bad {Bad} other {Miss}}',
  finishedTitle: 'Finished',
  finishedAutoplay: 'The autoplay run reached the end of the song.',

  // ---- results ----
  resultsTitle: 'Results',
  resultsNoNotes: 'No notes were played.',
  accuracy: 'Accuracy',
  accuracyValue: '{value}%',
  score: 'Score',
  maxCombo: 'Max combo',
  gradePerfect: 'Perfect',
  gradeGood: 'Good',
  gradeBad: 'Bad',
  gradeMiss: 'Miss',
  earlyLate: 'Early / late',
  earlyLateValue: '{early} / {late}',
  meanOffset: 'Average timing',
  meanValue: '{dir, select, early {{ms} ms early} late {{ms} ms late} other {on time}}',
  pending: 'Not reached',
  histogramLabel: 'Early and late hits; activate a bar to jump to its notes',
  histogramBar:
    '{count, plural, one {# note} other {# notes}} hit between {from} and {to} ms. Activate to select them in the editor.',
  histogramEarly: '{ms} ms (early)',
  histogramLate: '+{ms} ms (late)',
  timingHint:
    '{dir, select, early {Your hits were {ms} ms early on average.} other {Your hits were {ms} ms late on average.}} If that happens every time, calibrate the audio latency or check the offset in Song & Timing. The chart offset is never changed automatically.',
  openTiming: 'Open Song & Timing',
  calibrate: 'Calibrate latency',

  // ---- settings rows ----
  setScreen: 'Preview screen shape',
  setScreenDesc:
    'The screen the preview simulates. Players with other screens see the chart letterboxed or squeezed as Phira does.',
  setReadout: 'Show the line readout under the preview',
  setReadoutDesc: 'Position, rotation, opacity and speed of the active line at the playhead (Shift+I).',
  setTestMode: 'Test play mode',
  setTestModeDesc:
    'Autoplay shows the chart as Phira plays it; judged play lets you play it with the keyboard and mouse.',
  setLaneLinkDesc:
    'Hovering the note lane marks the spot on the line in the preview, and hovered notes are outlined in both.',
  setLeadIn: 'Test play lead-in (beats before the playhead)',
  setLeadInDesc: 'Test play from the playhead starts this many beats earlier.',

  // ---- help ----
  helpPreviewTitle: 'The preview',
  helpPreviewBody:
    'The preview is the real Phira render of your chart at the playhead. **Clean** shows only what players see; **Edit** adds line labels, outlines of invisible lines and hidden notes, and the gizmo. Click a note to select it, click a line to make it active (click again to cycle through overlapping lines), right-click for line actions. Ctrl+wheel zooms, middle drag pans.',
  helpGizmoTitle: 'Moving lines with the gizmo',
  helpGizmoBody:
    'Drag the centre to move the active line, the ring to rotate it and the end handles to change its length or size. A property with one keyframe changes for the whole chart; an animated one gets a keyframe at the playhead (position counts as a pair). At the Full level, **Auto-key** makes every drag write a keyframe. The ring turns in whole degrees. Hold Shift to lock an axis (15-degree steps on the ring), Ctrl to offset the whole motion, Alt to move or turn freely without snapping; Esc cancels.',
  helpTestPlayTitle: 'Test play',
  helpTestPlayBody:
    'F5 plays from 2 beats before the playhead, Shift+F5 from the start. In judged play any letter key or the left mouse button taps, a held key catches drags and holds, and Space or a mouse swipe flicks. The results show your accuracy and whether you hit early or late.',
});

export default catalogOf(m);
